/**
 * Step 7 — Overtime Types [TYPE B — Optional]
 *
 * Embeds OvertimeSettingsTab scoped to currentCompany.id.
 * Guard: if no company, show dependency notice instead of "Set Up Now".
 * Last step: "Finish Setup →" label on Next button.
 */

import { useState } from 'react';
import { Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WizardBottomBar } from '@/components/onboarding/WizardBottomBar';
import { OvertimeSettingsTab } from '@/components/settings/OvertimeSettingsTab';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import type { OnboardingStep } from '@/contexts/OnboardingContext';
import type { OvertimeSettings } from '@/types/timesheetSettings';

const PRIMARY = '#00796B';

const DEFAULT_OVERTIME_SETTINGS: OvertimeSettings = {
  enabled: false,
  thresholds: {
    mode: 'employee-pattern',
  },
  types: [],
};

interface Step7OvertimeTypesProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

export function Step7OvertimeTypes({ onNext, onBack }: Step7OvertimeTypesProps) {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;

  const [showEmbedded, setShowEmbedded] = useState(false);
  const [overtimeSettings, setOvertimeSettings] =
    useState<OvertimeSettings>(DEFAULT_OVERTIME_SETTINGS);

  const { overtimeTypes, refetch } = useOvertimeTypes({
    enabled: !!companyId && showEmbedded,
    screenId: 'settings-general',
  });

  const handleSkip = () => onNext('skipped');
  const handleNext = () => onNext(overtimeTypes.length > 0 ? 'completed' : 'skipped');
  const hasCompany = !!companyId;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        {!showEmbedded ? (
          <div className="flex flex-col items-center justify-center h-full text-center max-w-md mx-auto">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${PRIMARY}18` }}
            >
              <Timer className="w-7 h-7" style={{ color: PRIMARY }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No overtime types defined</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Overtime types let employees categorise additional hours worked beyond their contracted
              pattern. You can configure these later from the Companies section in Settings.
            </p>

            {hasCompany ? (
              <Button
                style={{ backgroundColor: PRIMARY, color: 'white' }}
                onClick={() => setShowEmbedded(true)}
              >
                Set Up Overtime Types Now
              </Button>
            ) : (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                Add a company in Step 1 before configuring overtime types.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <OvertimeSettingsTab
                settings={{
                  ...overtimeSettings,
                  types: overtimeTypes,
                }}
                onChange={(settings) => setOvertimeSettings(settings)}
                companyId={companyId}
                types={overtimeTypes}
                onTypesChange={refetch}
              />
            </div>
          </div>
        )}
      </div>

      <WizardBottomBar
        currentStep={7 as OnboardingStep}
        hideBack={false}
        hideSkip={!hasCompany}
        nextLabel="Finish Setup →"
        onBack={onBack}
        onSkip={handleSkip}
        onNext={handleNext}
      />
    </div>
  );
}
